import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  Eye,
  EyeOff,
  XCircle,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Login Form Header ── */

const LoginHeader = () => (
  <div className="bg-gradient-to-br from-sky-600 to-sky-700 px-8 py-10 text-center">
    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
      <ShieldCheck className="w-8 h-8 text-white" />
    </div>
    <h2 className="text-2xl font-bold text-white">Admin Access</h2>
    <p className="text-sky-100 mt-1 text-sm">NeatWave Cleaning Dashboard</p>
  </div>
);

/* ── Password Input Field ── */

const PasswordField = ({ password, showPassword, error, onChange, onToggleVisibility }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-700">Password</label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={onChange}
        placeholder="Enter admin password"
        className={`w-full pl-10 pr-12 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-transparent transition-colors ${
          error ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"
        }`}
        autoFocus
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
      >
        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    {error && (
      <p className="text-sm text-red-600 flex items-center gap-1.5">
        <XCircle className="w-3.5 h-3.5" />
        {error}
      </p>
    )}
  </div>
);

/* ── Login Form ── */

const LoginForm = ({ onLogin, error, shaking, submitting }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(password);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-inter">
      <div className="w-full max-w-md">
        <div
          className={`bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden transition-transform ${
            shaking ? "animate-shake" : ""
          }`}
          style={shaking ? { animation: "shake 0.5s ease-in-out" } : {}}
        >
          <LoginHeader />

          <div className="px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <PasswordField
                password={password}
                showPassword={showPassword}
                error={error}
                onChange={(e) => setPassword(e.target.value)}
                onToggleVisibility={() => setShowPassword((v) => !v)}
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-medium py-3 rounded-lg transition-colors shadow-sm text-sm disabled:opacity-60"
              >
                {submitting ? "Verifying..." : "Access Dashboard"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-sm text-slate-400 hover:text-sky-600 transition-colors inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to website
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};

/* ── Auth Gate Wrapper ── */

const AdminAuth = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dependencies explanation:
  // ✅ Included: setAuthenticated, setChecking (React setState - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  // ❌ Not included: cancelled (declared INSIDE useEffect - doesn't exist in outer scope, technically impossible to add)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/verify`, { withCredentials: true })
      .then(() => {
        if (!cancelled) setAuthenticated(true);
      })
      .catch(() => {
        // Not authenticated — show login
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, [setAuthenticated, setChecking]);

  // Dependencies explanation:
  // ✅ Included: shaking, setShaking (state & setState from useState - both stable)
  // ❌ Not included: timer (declared INSIDE useEffect - doesn't exist in outer scope, technically impossible to add)
  useEffect(() => {
    if (!shaking) return;
    const timer = setTimeout(() => setShaking(false), 500);
    return () => clearTimeout(timer);
  }, [shaking, setShaking]);

  // Dependencies explanation:
  // ✅ Included: setSubmitting, setError, setAuthenticated, setShaking (React setState - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLogin = useCallback(async (password) => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      setAuthenticated(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed. Please try again.";
      setError(msg);
      setShaking(true);
    } finally {
      setSubmitting(false);
    }
  }, [setSubmitting, setError, setAuthenticated, setShaking]);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-sky-600 animate-spin" />
      </div>
    );
  }

  if (authenticated) return children;

  return <LoginForm onLogin={handleLogin} error={error} shaking={shaking} submitting={submitting} />;
};

export const logout = async () => {
  try {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
  } catch (err) {
    // Best-effort logout attempt - page reloads immediately after
    // Network failures during logout are expected and non-critical
    console.error('Logout API call failed (non-critical):', err.message);
  }
  window.location.reload();
};

export default AdminAuth;
